import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { LogOut, RefreshCw, KeyRound, Power, Building2, CheckCircle2, Mail, Trash2, Download, Ban, Clock, FileText, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function SuperDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [trialReqs, setTrialReqs] = useState([]);
  const [trialDays, setTrialDays] = useState({});
  const [trialEdit, setTrialEdit] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/login", { replace: true });
    if (user && user.role === "practice_admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get("/super/practices", { headers: authHeaders }),
        api.get("/super/trial-requests", { headers: authHeaders }),
      ]);
      setRows(pRes.data);
      setTrialReqs(tRes.data);
      setTrialDays((prev) => {
        const next = { ...prev };
        tRes.data.forEach((tr) => {
          if (next[tr.id] === undefined) next[tr.id] = tr.trial_days || 14;
        });
        return next;
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    // eslint-disable-next-line
    if (user && user.role === "superadmin") load();
  }, [user]);

  const approveTrial = async (id, email) => {
    const days = parseInt(trialDays[id], 10) || 14;
    if (days < 1 || days > 365) { toast.error("Bitte 1–365 Trial-Tage eingeben."); return; }
    if (!window.confirm(`Testzugang für "${email}" mit ${days} Trial-Tagen freischalten? Es wird automatisch ein Registrierungslink per E-Mail versendet.`)) return;
    try {
      const { data } = await api.post(`/super/trial-requests/${id}/approve`, { trial_days: days }, { headers: authHeaders });
      toast.success(data.email_sent ? `Freigeschaltet (${data.trial_days} Tage) – Registrierungslink versendet.` : `Freigeschaltet (${data.trial_days} Tage). Hinweis: E-Mail-Versand nicht bestätigt (Resend-Domain prüfen).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const rejectTrial = async (id, email) => {
    if (!window.confirm(`Anfrage von "${email}" wirklich ablehnen und löschen?`)) return;
    try {
      await api.delete(`/super/trial-requests/${id}`, { headers: authHeaders });
      toast.success("Anfrage abgelehnt.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (id) => {
    try {
      await api.patch(`/super/practices/${id}/active`, {}, { headers: authHeaders });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resetPw = async (id, name) => {
    if (!window.confirm(`Passwort für "${name}" wirklich zurücksetzen?`)) return;
    try {
      const { data } = await api.post(`/super/practices/${id}/reset-password`, {}, { headers: authHeaders });
      window.prompt(`Neues Passwort für ${name} (einmalig anzeigen, bitte sicher übergeben):`, data.new_password);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setTrialPeriod = async (id, name) => {
    const days = parseInt(trialEdit[id] ?? 14, 10);
    if (!days || days < 1 || days > 365) { toast.error("Bitte 1–365 Tage eingeben."); return; }
    if (!window.confirm(`Testzeitraum von "${name}" auf ${days} Tage ab heute setzen?`)) return;
    try {
      const { data } = await api.patch(`/super/practices/${id}/trial`, { trial_days: days }, { headers: authHeaders });
      toast.success(`Testzeitraum gesetzt: ${days} Tage – bis ${new Date(data.trial_ends_at).toLocaleDateString("de-DE")}.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cancelPractice = async (id, name) => {
    if (!window.confirm(`Praxis "${name}" als gekündigt markieren?\n\nDie Praxis wird deaktiviert und es beginnt eine 30-Tage-Frist, in der die Daten exportiert (zurückgegeben) werden können.`)) return;
    try {
      const { data } = await api.post(`/super/practices/${id}/cancel`, {}, { headers: authHeaders });
      const dl = new Date(data.export_deadline).toLocaleDateString("de-DE");
      toast.success(`Gekündigt. Export der Daten möglich bis ${dl} (${data.grace_days} Tage).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const grantAccess = async (id, name) => {
    const input = window.prompt(
      `„${name}" jetzt manuell freigeben (z. B. Überweisung ist unterwegs, Zahlung noch nicht bei Mollie).\n\nFür wie viele Tage soll der Zugang freigeschaltet werden?\n\nWichtig: Sobald die Zahlung bei Mollie eingeht, wird die Praxis automatisch regulär aktiviert – die manuelle Freigabe stört diesen Automatismus NICHT.`,
      "14"
    );
    if (input === null) return;
    const days = parseInt(input, 10);
    if (!days || days < 1 || days > 90) { toast.error("Bitte 1–90 Tage eingeben."); return; }
    try {
      const { data } = await api.post(`/super/practices/${id}/grant-access`, { days }, { headers: authHeaders });
      toast.success(`Zugang manuell freigegeben bis ${new Date(data.manual_access_until).toLocaleDateString("de-DE")} (${days} Tage).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const revokeAccess = async (id, name) => {
    if (!window.confirm(`Manuelle Freigabe von „${name}" aufheben?\n\nDie Praxis fällt danach auf ihren regulären Zahlungs-/Abo-Status zurück.`)) return;
    try {
      await api.delete(`/super/practices/${id}/grant-access`, { headers: authHeaders });
      toast.success("Manuelle Freigabe aufgehoben.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const retriggerInvoice = async (id, name) => {    if (!window.confirm(`Für "${name}" eine NEUE Überweisungs-Rechnung (69,99 €) ausstellen?\n\nEs wird eine frische Mollie-Zahlung erzeugt und die Rechnung über LAIzyFinance an den Kunden versendet. Nutze dies, wenn die vorige Rechnung abgelaufen ist.`)) return;
    try {
      toast.info("Neue Rechnung wird erstellt …");
      const { data } = await api.post(`/super/practices/${id}/retrigger-invoice`, {}, { headers: authHeaders });
      toast.success(`Neue Rechnung ausgestellt (${data.amount.toFixed(2)} €). Zahlung ${data.mollie_payment_id} – LAIzyFinance versendet die Rechnung.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportData = async (id, name) => {    try {
      toast.info("Export wird erstellt …");
      const res = await api.get(`/super/practices/${id}/export`, { headers: authHeaders, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (name || "praxis").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `export_${safe}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Datenexport heruntergeladen.");
    } catch (e) { toast.error("Export fehlgeschlagen."); }
  };

  const deleteData = async (id, name) => {
    const entered = window.prompt(`ENDGÜLTIGE LÖSCHUNG aller Daten von "${name}".\n\nDies kann nicht rückgängig gemacht werden. Alle Einwilligungen, PDFs, Texte und Konten werden gelöscht.\n\nZur Bestätigung bitte den exakten Praxisnamen eingeben:`);
    if (entered === null) return;
    if (entered.trim() !== name.trim()) { toast.error("Name stimmt nicht überein. Löschung abgebrochen."); return; }
    try {
      const { data } = await api.delete(`/super/practices/${id}/data`, { headers: authHeaders, data: { confirm_name: entered.trim() } });
      const c = data.deleted_counts;
      toast.success(`Gelöscht: ${c.consents} Einwilligungen, ${c.users} Konten, ${c.privacy_policies + c.treatment_contracts} Dokumentversionen.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!user || user === false) return <div className="min-h-screen grid place-items-center text-stone-400">Lädt…</div>;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="max-w-6xl mx-auto px-6 md:px-10 pt-8 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500">Super-Admin</div>
            <div className="font-display text-lg font-semibold text-emerald-900">Praxen verwalten</div>
          </div>
        </div>
        <Button data-testid="super-logout-button" variant="secondary"
          onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
          className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
          <LogOut className="mr-2 w-4 h-4" /> Abmelden
        </Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-6 space-y-6">
        {trialReqs.length > 0 && (
          <div data-testid="super-trial-banner" className="bg-amber-50 border border-amber-200 rounded-3xl p-6 md:p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-amber-900">
                  {trialReqs.length} Testanfrage{trialReqs.length === 1 ? "" : "n"}
                </h2>
                <p className="text-sm text-amber-700">Verifizierte Interessenten – nach Freischaltung erhalten sie automatisch einen Registrierungslink.</p>
              </div>
            </div>
            <div className="space-y-2">
              {trialReqs.map((tr) => (
                <div key={tr.id} data-testid={`trial-row-${tr.id}`}
                  className="flex items-center justify-between gap-3 bg-white rounded-2xl border border-amber-100 px-4 py-3 flex-wrap">
                  <div>
                    <div className="font-medium text-stone-900">{tr.email}</div>
                    <div className="text-sm text-stone-500">
                      {tr.status === "approved"
                        ? `Link gesendet${tr.approved_at ? ` · ${new Date(tr.approved_at).toLocaleDateString("de-DE")}` : ""} · wartet auf Registrierung`
                        : `verifiziert${tr.verified_at ? ` am ${new Date(tr.verified_at).toLocaleDateString("de-DE")}` : ""}`}
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <div className="flex items-center gap-1.5">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        data-testid={`trial-days-input-${tr.id}`}
                        value={trialDays[tr.id] ?? 14}
                        onChange={(e) => setTrialDays((p) => ({ ...p, [tr.id]: e.target.value }))}
                        className="h-11 w-20 rounded-xl text-center"
                      />
                      <span className="text-sm text-stone-500">Tage</span>
                    </div>
                    {tr.status === "verified" && (
                      <Button data-testid={`trial-approve-${tr.id}`} onClick={() => approveTrial(tr.id, tr.email)}
                        className="h-11 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
                        <CheckCircle2 className="mr-2 w-4 h-4" /> Freischalten
                      </Button>
                    )}
                    {tr.status === "approved" && (
                      <Button data-testid={`trial-resend-${tr.id}`} onClick={() => approveTrial(tr.id, tr.email)}
                        variant="secondary" className="h-11 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                        <RefreshCw className="mr-2 w-4 h-4" /> Link erneut senden
                      </Button>
                    )}
                    <Button data-testid={`trial-reject-${tr.id}`} onClick={() => rejectTrial(tr.id, tr.email)}
                      variant="ghost" className="h-11 w-11 rounded-xl text-orange-600 hover:bg-orange-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-display text-2xl font-semibold text-emerald-900">Alle Praxen</h1>
            <Button data-testid="super-refresh-button" variant="secondary" onClick={load}
              className="h-12 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
              <RefreshCw className="mr-2 w-4 h-4" /> Aktualisieren
            </Button>
          </div>

          <div className="rounded-2xl border border-stone-100 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50 hover:bg-stone-50">
                  <TableHead className="py-4 text-stone-600">Praxis</TableHead>
                  <TableHead className="py-4 text-stone-600">Admin-E-Mail</TableHead>
                  <TableHead className="py-4 text-stone-600">Einwilligungen</TableHead>
                  <TableHead className="py-4 text-stone-600">Status</TableHead>
                  <TableHead className="py-4 text-stone-600 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Lädt…</TableCell></TableRow>}
                {!loading && rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-12 text-stone-400">Keine Praxen registriert.</TableCell></TableRow>}
                {rows.map((p) => (
                  <TableRow key={p.id} data-testid={`practice-row-${p.id}`}>
                    <TableCell className="py-4 font-medium text-stone-900">{p.name}</TableCell>
                    <TableCell className="py-4 text-stone-600">{p.admin_email || "—"}</TableCell>
                    <TableCell className="py-4 text-stone-600">{p.consent_count}</TableCell>
                    <TableCell className="py-4">
                      {p.cancelled_at ? (
                        <div className="space-y-1">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
                            Gekündigt{p.cancelled_by === "practice" ? " (durch Praxis)" : p.cancelled_by === "super" ? " (durch Super-Admin)" : ""}
                          </span>
                          {p.export_deadline && (() => {
                            const days = Math.ceil((new Date(p.export_deadline) - new Date()) / 86400000);
                            return (
                              <div data-testid={`cancel-deadline-${p.id}`} className="text-xs text-stone-500">
                                Export bis {new Date(p.export_deadline).toLocaleDateString("de-DE")}
                                {days >= 0 ? ` · noch ${days} Tag${days === 1 ? "" : "e"}` : " · Frist abgelaufen"}
                              </div>
                            );
                          })()}
                        </div>
                      ) : p.active ? (
                        <div className="space-y-1">
                          {p.access_state === "active" ? (
                            <span data-testid={`access-badge-${p.id}`} className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-900">Aktiv</span>
                          ) : (
                            <span data-testid={`access-badge-${p.id}`} className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Zugang gesperrt · Zahlung ausstehend</span>
                          )}
                          {(p.first_paid_at || p.billing_method === "directdebit") && (
                            <div data-testid={`billing-method-${p.id}`} className="text-xs text-stone-500">
                              Zahlart: {p.billing_method === "directdebit" ? `SEPA-Lastschrift${p.sepa_info?.iban_last4 ? ` (•••• ${p.sepa_info.iban_last4})` : ""}${p.first_paid_at ? "" : " · Ersteinzug ausstehend"}`
                                : p.billing_method === "banktransfer" ? "Überweisung" : "Karte/Sofort"}
                            </div>
                          )}
                          {(() => {
                            const now = new Date();
                            const trialEnd = p.trial_ends_at ? new Date(p.trial_ends_at) : null;
                            const paidUntil = p.paid_until ? new Date(p.paid_until) : null;
                            const manualUntil = p.manual_access_until ? new Date(p.manual_access_until) : null;
                            const isPaid = p.subscription_status === "active" && paidUntil && paidUntil > now;
                            if (isPaid) {
                              return <div data-testid={`trial-info-${p.id}`} className="text-xs text-emerald-700">Abo aktiv · bezahlt bis {paidUntil.toLocaleDateString("de-DE")}</div>;
                            }
                            if (manualUntil && manualUntil > now) {
                              return <div data-testid={`manual-access-info-${p.id}`} className="text-xs text-sky-700">Manuell freigegeben bis {manualUntil.toLocaleDateString("de-DE")} · Zahlung ausstehend</div>;
                            }
                            if (trialEnd) {
                              const days = Math.ceil((trialEnd - now) / 86400000);
                              if (days >= 0) {
                                return <div data-testid={`trial-info-${p.id}`} className="text-xs text-stone-500">Test: noch {days} Tag{days === 1 ? "" : "e"} (bis {trialEnd.toLocaleDateString("de-DE")})</div>;
                              }
                              return <div data-testid={`trial-info-${p.id}`} className="text-xs text-amber-700">Test abgelaufen · Zahlung ausstehend</div>;
                            }
                            if (p.subscription_status === "payment_required") {
                              return <div data-testid={`trial-info-${p.id}`} className="text-xs text-amber-700">Zahlung ausstehend</div>;
                            }
                            return null;
                          })()}
                        </div>
                      ) : p.approved ? (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Gesperrt</span>
                      ) : (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Wartet auf Freischaltung</span>
                      )}
                    </TableCell>
                    <TableCell className="py-4 text-right space-x-2 space-y-2">
                      {!p.cancelled_at && (
                        <div className="inline-flex items-center gap-1.5 align-middle">
                          <Input type="number" min={1} max={365} data-testid={`trial-set-input-${p.id}`}
                            value={trialEdit[p.id] ?? 14}
                            onChange={(e) => setTrialEdit((m) => ({ ...m, [p.id]: e.target.value }))}
                            className="h-10 w-16 rounded-xl text-center" />
                          <Button data-testid={`trial-set-${p.id}`} onClick={() => setTrialPeriod(p.id, p.name)}
                            variant="secondary" className="h-10 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800">
                            <Clock className="mr-2 w-4 h-4" /> Test setzen
                          </Button>
                        </div>
                      )}
                      <Button data-testid={`reset-pw-${p.id}`} onClick={() => resetPw(p.id, p.name)}
                        variant="secondary" className="h-10 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                        <KeyRound className="mr-2 w-4 h-4" /> PW zurücksetzen
                      </Button>
                      <Button data-testid={`export-${p.id}`} onClick={() => exportData(p.id, p.name)}
                        variant="secondary" className="h-10 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-900">
                        <Download className="mr-2 w-4 h-4" /> Daten exportieren
                      </Button>
                      {p.billing_method === "banktransfer" && !p.cancelled_at && (
                        <Button data-testid={`retrigger-invoice-${p.id}`} onClick={() => retriggerInvoice(p.id, p.name)}
                          variant="secondary" className="h-10 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-800">
                          <FileText className="mr-2 w-4 h-4" /> Neue Rechnung
                        </Button>
                      )}
                      {!p.cancelled_at && (
                        (p.manual_access_until && new Date(p.manual_access_until) > new Date()) ? (
                          <Button data-testid={`revoke-access-${p.id}`} onClick={() => revokeAccess(p.id, p.name)}
                            variant="secondary" className="h-10 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700">
                            <Ban className="mr-2 w-4 h-4" /> Freigabe aufheben
                          </Button>
                        ) : (
                          <Button data-testid={`grant-access-${p.id}`} onClick={() => grantAccess(p.id, p.name)}
                            variant="secondary" className="h-10 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800">
                            <Unlock className="mr-2 w-4 h-4" /> Jetzt freigeben
                          </Button>
                        )
                      )}
                      <Button data-testid={`toggle-${p.id}`} onClick={() => toggleActive(p.id)}
                        className={`h-10 rounded-xl ${p.active ? "bg-orange-600 hover:bg-orange-700" : "bg-emerald-900 hover:bg-emerald-800"} text-white`}>
                        <Power className="mr-2 w-4 h-4" /> {p.active ? "Deaktivieren" : (p.approved ? "Aktivieren" : "Freischalten")}
                      </Button>
                      {!p.cancelled_at ? (
                        <Button data-testid={`cancel-${p.id}`} onClick={() => cancelPractice(p.id, p.name)}
                          variant="secondary" className="h-10 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700">
                          <Ban className="mr-2 w-4 h-4" /> Kündigen
                        </Button>
                      ) : (
                        <Button data-testid={`delete-data-${p.id}`} onClick={() => deleteData(p.id, p.name)}
                          className="h-10 rounded-xl bg-rose-600 hover:bg-rose-700 text-white">
                          <Trash2 className="mr-2 w-4 h-4" /> Endgültig löschen
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}
